// src/components/PrivacyPolicyPage.js

import React from 'react';
import { Link } from 'react-router-dom'; // Optional: if you need links within the policy

function PrivacyPolicyPage() {
  // Get current year for date placeholder or update manually
  const currentYear = new Date().getFullYear();

  return (
    <div className="container mt-4 mb-5">
      <div className="row justify-content-center">
        <div className="col-lg-10 col-xl-8">
          <h1 className="mb-4 text-center">Privacy Policy</h1>
          {/* *** UPDATE this date when you finalize the policy *** */}
          <p className="text-muted text-center mb-4">Last Updated: May 4, 2025</p>

          {/* Introduction */}
          <section className="mb-4">
            <h2>1. Introduction</h2>
            <p>
              Welcome to Church Aware ("we," "us," or "our"). This Privacy Policy describes how we collect, use, disclose, and protect information obtained from users ("you," "your") of the Church Aware software application and related services (collectively, the "Service").
            </p>
            <p>
              By accessing or using the Service, you signify that you have read, understood, and agree to our collection, storage, use, and disclosure of your personal information as described in this Privacy Policy and our Terms of Service (if applicable). If you do not agree with this policy, please do not access or use the Service.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-4">
            <h2>2. Information We Collect</h2>
            <p>
              We collect information primarily provided by the client organizations (e.g., churches, hereafter "Organizations") that use our Service, as well as information you may provide directly when creating or managing your user account. This information may include, but is not limited to:
            </p>
            <ul>
              <li>Contact Information (e.g., name, email address, phone number)</li>
              <li>Demographic Information (e.g., birthday, address, city, state, zip code)</li>
              <li>Family Relationship Information (as provided by your Organization)</li>
              <li>Account Credentials (e.g., username, password - hashed and stored securely)</li>
              {/* Add other specific examples if relevant */}
              <li>Group affiliations, photos, skills, or other profile details entered by you or your Organization's administrator.</li>
            </ul>
             <p>
                We act as a data processor for the information provided by Organizations. The Organization is the data controller and determines the purposes and means of processing personal data within their directory.
             </p>
          </section>

          {/* How We Use Information */}
          <section className="mb-4">
            <h2>3. How We Use Information</h2>
             {/* List remains the same */}
            <p>We use the information we collect for purposes such as:</p>
             <ul> <li>Providing, operating, and maintaining the Service.</li> <li>Allowing Organizations to manage their directories.</li> <li>Allowing authorized users (members) to access directory information as permitted by their Organization.</li> <li>Enabling users to update their own profile information (where applicable).</li> <li>Authenticating users and managing login sessions.</li> <li>Communicating with administrators or users regarding service updates or support.</li> <li>Improving and optimizing the Service.</li> <li>Ensuring the security and integrity of the Service.</li> </ul>
          </section>

          {/* Information Sharing and Disclosure */}
          <section className="mb-4">
            <h2>4. Information Sharing and Disclosure</h2>
             {/* List remains the same */}
            <p>We understand the sensitivity of your personal information.</p>
             <ul> <li><strong>We do not sell your personal information to third parties.</strong></li> <li>Information within a specific church directory is accessible only to authorized users associated with that Organization, as determined by the Organization's administrators.</li> <li>We may disclose information if required by law, subpoena, or other legal process, or if we have a good faith belief that disclosure is reasonably necessary to (a) investigate, prevent, or take action regarding suspected or actual illegal activities or to assist government enforcement agencies; (b) enforce our agreements with you or the Organization; (c) investigate and defend ourselves against any third-party claims or allegations; (d) protect the security or integrity of our Service; or (e) exercise or protect the rights and safety of Church Aware, our users, personnel, or others.</li> <li>We may use third-party service providers to help operate the Service. These providers will only have access to your information as reasonably necessary to perform their tasks on our behalf and are obligated not to disclose or use it for other purposes.</li> </ul>
          </section>

          {/* Data Security */}
          <section className="mb-4">
            <h2>5. Data Security</h2>
             {/* Paragraph remains the same */}
            <p> We implement technical and organizational measures designed to protect your personal information. Personal information is encrypted during transmission (using SSL/HTTPS) and stored using generally accepted industry standards, including leveraging security features provided by our hosting infrastructure (e.g., Google Cloud Platform/Firebase). However, please be aware that no security measures are perfect or impenetrable, and we cannot guarantee the absolute security of your information. Transmission of information over the internet is inherently insecure. </p>
          </section>

          {/* Data Retention & Removal */}
          <section className="mb-4">
            <h2>6. Data Retention and Removal</h2>
            {/* Paragraph remains the same */}
            <p> We retain personal information for as long as necessary to provide the Service to the Organization and its users, or as required by applicable law. Information associated with an Organization's directory can typically be managed and removed by the Organization's administrator. Individual users may also have options to manage their profile information. If you wish to have your information removed and cannot do so through your Organization or profile settings, please contact your Organization's administrator first. </p>
          </section>

          {/* Cookies and Tracking Technologies */}
          <section className="mb-4">
            <h2>7. Cookies and Tracking Technologies</h2>
             {/* Paragraph remains the same */}
            <p> We use cookies primarily for essential functions such as login session management and maintaining application state. We do not use cookies to track your activity across unrelated third-party websites. By using the Service, you consent to the use of these necessary cookies. </p>
          </section>

          {/* *** NEW SECTION: Children's Privacy *** */}
          <section className="mb-4">
            <h2>8. Children's Privacy</h2>
            <p>
              The Service is not intended for direct use by children under the age of 13 (or a higher age threshold if required by applicable local law). We do not knowingly collect personal information directly from children under this age. Information about children may be entered into the directory by authorized adults (e.g., parents, guardians, Organization administrators) solely for the purpose of the church directory as managed by the Organization.
            </p>
            <p>
              If you are a parent or guardian and believe that your child under 13 has provided personal information to us directly without your consent, please contact us at the email address provided below. If we become aware that we have collected personal information directly from a child under 13 without verification of parental consent, we will take steps to remove that information from our servers. We encourage parents and guardians to monitor their children's online activities.
            </p>
          </section>

          {/* *** NEW SECTION: Your Data Rights *** */}
          <section className="mb-4">
            <h2>9. Your Data Rights</h2>
            <p>
              Depending on your location and applicable data protection laws (such as GDPR, CCPA/CPRA, VCDPA), you may have certain rights regarding your personal information. These rights may include:
            </p>
            <ul>
              <li><strong>Access:</strong> The right to request access to the personal information we process about you.</li>
              <li><strong>Correction (Rectification):</strong> The right to request correction of inaccurate personal information. You may be able to update some information directly through your user profile or by contacting your Organization's administrator.</li>
              <li><strong>Deletion (Erasure):</strong> The right to request deletion of your personal information, subject to certain exceptions (e.g., legal obligations, organizational needs as determined by the data controller Organization).</li>
              <li><strong>Portability:</strong> The right, in certain circumstances, to receive your personal information in a structured, commonly used, and machine-readable format.</li>
              {/* Add other rights like Restriction, Objection if applicable/required */}
            </ul>
            <p>
              To exercise these rights, please contact your Organization's administrator first, as they are the data controller for the directory content. If you require further assistance or have questions about your rights in relation to Church Aware as a service provider, you may contact us using the contact information below. We will respond to requests in accordance with applicable data protection laws.
            </p>
          </section>

          {/* *** NEW SECTION: International Data Transfers *** */}
          <section className="mb-4">
            <h2>10. International Data Transfers</h2>
            <p>
              Your information may be transferred to, stored, and processed in the United States and potentially other countries where our servers or third-party service providers are located. Data protection laws in these countries may differ from those in your jurisdiction. By using the Service, you consent to the transfer of your information to countries outside your country of residence, including the United States.
            </p>
          </section>

          {/* *** NEW SECTION: Third-Party Links *** */}
           <section className="mb-4">
            <h2>11. Third-Party Links</h2>
            <p>
              The Service may occasionally contain links to external websites or services not operated by us (e.g., links within directory notes, links to app stores). This Privacy Policy does not apply to third-party websites or services. We are not responsible for the privacy practices of these third parties, and we encourage you to review their privacy policies before providing them with any information.
            </p>
           </section>

           {/* *** NEW SECTION: Do Not Track Signals *** */}
           <section className="mb-4">
            <h2>12. Do Not Track Signals</h2>
            <p>
              Some web browsers incorporate a "Do Not Track" (DNT) or similar feature that signals to websites that a user does not want to have their online activity tracked. As there is not yet a universally accepted standard for how to respond to DNT signals, our Service does not currently respond to DNT browser signals or mechanisms.
            </p>
           </section>

           {/* *** NEW SECTION: Governing Law *** */}
           <section className="mb-4">
            <h2>13. Governing Law</h2>
            <p>
              This Privacy Policy shall be governed by and construed in accordance with the laws of the State of North Carolina, United States, without regard to its conflict of law principles.
            </p>
           </section>

          {/* Disclaimer of Warranties (Renumbered) */}
          <section className="mb-4">
            <h2 className="text-danger">14. Disclaimer of Warranties</h2>
            {/* Content remains the same */}
            <p className="fw-bold"> THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED... NO ADVICE OR INFORMATION... WILL CREATE ANY WARRANTY NOT EXPRESSLY STATED HEREIN. </p>
          </section>

          {/* Limitation of Liability (Renumbered) */}
          <section className="mb-4">
            <h2 className="text-danger">15. Limitation of Liability</h2>
            {/* Content remains the same */}
            <p className="fw-bold"> TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL CHURCH AWARE... BE LIABLE FOR ANY INDIRECT, PUNITIVE... DAMAGES... </p> <p className="fw-bold"> THE CHURCH AWARE PARTIES ASSUME NO LIABILITY OR RESPONSIBILITY FOR ANY (I) ERRORS... (II) PERSONAL INJURY... (III) ANY UNAUTHORIZED ACCESS... (IV) ANY INTERRUPTION... (V) ANY BUGS... AND/OR (VI) ANY ERRORS OR OMISSIONS... </p> <p className="fw-bold"> OUR AGGREGATE LIABILITY FOR ALL CLAIMS RELATING TO THE SERVICE SHALL NOT EXCEED THE GREATER OF ONE HUNDRED U.S. DOLLARS ($100) OR THE AMOUNTS PAID BY YOU TO US FOR THE PAST THREE MONTHS OF THE SERVICE IN QUESTION. </p>
          </section>

          {/* Assumption of Risk (Renumbered) */}
          <section className="mb-4">
            <h2 className="text-danger">16. Assumption of Risk</h2>
            {/* Content remains the same */}
            <p className="fw-bold"> YOUR USE OF THE SERVICE IS ENTIRELY AT YOUR OWN RISK. YOU ASSUME FULL RESPONSIBILITY FOR ALL RISKS ASSOCIATED WITH YOUR USE OF THE SERVICE... YOU ACKNOWLEDGE THAT YOU HAVE BEEN ADVISED TO TAKE APPROPRIATE PRECAUTIONS... </p>
          </section>

          {/* Changes to This Policy (Renumbered) */}
          <section className="mb-4">
            <h2>17. Changes to This Privacy Policy</h2>
             {/* Content remains the same */}
            <p> We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes... </p>
          </section>

          {/* Contact Information (Renumbered) */}
          <section className="mb-4">
            <h2>18. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy, please contact us at: contact@churchaware.com
            </p>
            {/* You might want to add a physical address if required/desired */}
          </section>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicyPage;